/**
 * Device Fingerprinting Utility
 * 
 * Generates a privacy-safe device fingerprint using publicly available browser data.
 * This fingerprint is used for additional security validation without requiring
 * privacy consent under most privacy laws.
 */

class DeviceFingerprinting {
    constructor() {
        this.fingerprint = null;
        this.initialized = false;
    }

    /**
     * Generate privacy-safe device fingerprint
     * @returns {string} Base64 encoded device fingerprint hash
     */
    generateSafeDeviceFingerprint() {
        const fingerprint = {
            // HTTP headers (safe - automatically sent)
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages?.join(',') || '',
            platform: navigator.platform,
            
            // Basic capabilities (safe - standard JavaScript APIs)
            cookieEnabled: navigator.cookieEnabled,
            hardwareConcurrency: navigator.hardwareConcurrency || 0,
            
            // Screen info (basic, safe)
            screenResolution: `${screen.width}x${screen.height}`,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth || screen.colorDepth,
            
            // Timezone (functional data)
            timezoneOffset: new Date().getTimezoneOffset(),
            
            // Math operations (device-specific implementations)
            mathFingerprint: [
                Math.tan(-1e300),
                Math.sin(1e300),
                Math.cos(1e300)
            ].join(','),
            
            // Browser features (safe - capability detection)
            features: [
                typeof(Storage) !== "undefined" ? 'localStorage' : '',
                typeof(indexedDB) !== "undefined" ? 'indexedDB' : '',
                typeof(Worker) !== "undefined" ? 'webWorker' : '',
                CSS.supports('display', 'grid') ? 'grid' : '',
                CSS.supports('display', 'flex') ? 'flex' : '',
                CSS.supports('position', 'sticky') ? 'sticky' : ''
            ].filter(Boolean).join(','),

            // Date/Number formatting (locale-specific)
            dateString: new Date().toString().substring(0, 33), // Truncate for privacy
            numberFormat: (1234567.890123).toLocaleString().substring(0, 15),

            // Performance characteristics (device-specific)
            deviceMemory: navigator.deviceMemory || 0,
            maxTouchPoints: navigator.maxTouchPoints || 0
        };
        
        // Create deterministic hash of all safe data
        const fingerprintString = JSON.stringify(fingerprint);
        
        // Simple hash function for client-side (synchronous)
        let hash = 0;
        for (let i = 0; i < fingerprintString.length; i++) {
            const char = fingerprintString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        
        // Convert to positive hex string
        const hexHash = Math.abs(hash).toString(16);
        
        // Return base64-like string without special characters
        return btoa(hexHash).replace(/[+/=]/g, '').substring(0, 32);
    }

    /**
     * Get or generate device fingerprint
     * @returns {string} Device fingerprint
     */
    getFingerprint() {
        if (!this.initialized) {
            this.fingerprint = this.generateSafeDeviceFingerprint();
            this.initialized = true;
            
            // Store in sessionStorage for consistency across requests
            try {
                sessionStorage.setItem('deviceFingerprint', this.fingerprint);
            } catch (error) {
                console.warn('Unable to store device fingerprint in sessionStorage:', error);
            }
        }
        
        return this.fingerprint;
    }

    /**
     * Initialize device fingerprinting
     * Called once during app startup
     */
    initialize() {
        // Try to get existing fingerprint from sessionStorage
        try {
            const stored = sessionStorage.getItem('deviceFingerprint');
            if (stored && stored.length === 32) {
                this.fingerprint = stored;
                this.initialized = true;
                return this.fingerprint;
            }
        } catch (error) {
            console.warn('Unable to retrieve device fingerprint from sessionStorage:', error);
        }
        
        // Generate new fingerprint if none exists
        return this.getFingerprint();
    }

    /**
     * Reset device fingerprint (for testing or security reasons)
     */
    reset() {
        this.fingerprint = null;
        this.initialized = false;
        
        try {
            sessionStorage.removeItem('deviceFingerprint');
        } catch (error) {
            console.warn('Unable to remove device fingerprint from sessionStorage:', error);
        }
        
        return this.getFingerprint();
    }

    /**
     * Validate current environment supports fingerprinting
     * @returns {boolean} True if fingerprinting is supported
     */
    isSupported() {
        return !!(
            navigator.userAgent &&
            screen.width &&
            screen.height &&
            navigator.language
        );
    }
}

// Create and export singleton instance
const deviceFingerprinting = new DeviceFingerprinting();

export default deviceFingerprinting;
